import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { theme } from '../../../theme/theme'
import { BodyLarge } from '../../../components/styles/Typography'
import { TabData, Tabs } from '../../../components/Tabs'
import styled from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import {
  useIsAnyScreeningHandled,
  useIsScreeningDue,
  useSelectScreeningStatus,
  useSelectUserGender,
} from '../../../state/selectors'
import { Gender } from '../../../api/ester-b2b-api/private/users/types'
import { useTranslation } from 'react-i18next'
import { HealthPlanV2 } from '../yourHealthV2/HealthPlanV2'
import { Navigate, useParams } from 'react-router-dom'
import { ScreeningModal } from '../../../components/yourHealth/ScreeningModal'
import { useGetEntryById } from '../../../api/contentful/contentfulQueries'
import { TextWithImageCard } from '../../../components/TextWithImageCard'
import { isITextWithImageBlock, ScreeningStatus } from '@estercare/ester-shared'
import { RenewScreening } from '../../../components/RenewScreening'
import { AskUsWidget } from '../../../components/AskUsWidget'
import { HealthProgress } from '../yourHealthV2/HealthProgress'

const SCREENING_MODAL_DISMISSED_KEY = 'screeningModalDismissed'

export const YourHealthIndex = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const screeningStatus = useSelectScreeningStatus()
  const isScreeningDue = useIsScreeningDue()
  const isAnyHandled = useIsAnyScreeningHandled()
  const isFemaleUser = useSelectUserGender() === Gender.F

  const [showScreeningModal, setShowScreeningModal] = useState(() => {
    return sessionStorage.getItem(SCREENING_MODAL_DISMISSED_KEY) !== 'true'
  })

  const screeningBlock = useGetEntryById('7s3RkHs2YxV61gDavGwJku').data

  const handleCloseModal = () => {
    sessionStorage.setItem(SCREENING_MODAL_DISMISSED_KEY, 'true')
    setShowScreeningModal(false)
  }

  const tabsData: TabData[] = [
    { label: t('employee.yourHealth.healthPlanTab'), content: <HealthPlanV2 />, slug: 'health-plan' },
    { label: t('employee.yourHealth.healthProgressTab'), content: <HealthProgress />, slug: 'health-progress' },
  ]

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
      })
    }
  }, [])

  if (!screeningStatus) return null
  // The analysis tab was part of the retired V1 health plan; old links still point to it.
  if (id === 'analysis') {
    return <Navigate to="/portal/your-health/health-plan" replace />
  }

  return (
    <Container ref={containerRef}>
      {isScreeningDue && showScreeningModal && isFemaleUser && <ScreeningModal onClose={handleCloseModal} />}
      {(screeningStatus === ScreeningStatus.RENEW || !isAnyHandled) && (
        <ComponentLayout paddingTop={0}>
          {screeningStatus === ScreeningStatus.RENEW && <RenewScreening />}
          {!isAnyHandled && (
            <BodyLarge style={{ whiteSpace: 'pre-line' }}>{t('employee.yourHealth.noScreeningDescription')}</BodyLarge>
          )}
        </ComponentLayout>
      )}
      {isAnyHandled ? (
        <Tabs tabs={tabsData} />
      ) : (
        <ComponentLayout paddingTop={0}>
          {isITextWithImageBlock(screeningBlock) ? (
            <TextWithImageCard {...screeningBlock.fields} />
          ) : (
            <BodyLarge>{t('employee.yourHealth.pendingMessage')}</BodyLarge>
          )}
        </ComponentLayout>
      )}
      {id === 'health-plan' && <AskUsWidget />}
    </Container>
  )
}

const Container = styled.div`
  height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-top: ${theme.spacing.xlarge}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding-top: ${theme.spacing.mediumLarge}px;
  }
`
